import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Pencil, Plus, Save, Trash2, X } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, W1GrammarCategory, W1GrammarCategoryInput, W1GrammarStructure } from '../utils/api';
import { useAuthStore } from '../store';

type CategoryDraft = {
  id?: string;
  name: string;
  description: string;
  structures: W1GrammarStructure[];
  sortOrder: number;
  isActive: boolean;
  isFallback: boolean;
};

const emptyStructure = (): W1GrammarStructure => ({ pattern: '', example: '', note: '' });

const AdminW1GrammarCategories: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { token } = useAuthStore();
  const levelParam = Number(searchParams.get('level') || 3);
  const level = levelParam === 4 ? 4 : 3;
  const [categories, setCategories] = useState<W1GrammarCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [draft, setDraft] = useState<CategoryDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const loadCategories = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const response = await api.getAdminW1GrammarCategories(token, '2.0', level);
      setCategories(response.categories || []);
    } catch (loadError: any) {
      setError(loadError?.message || 'Failed to load grammar categories.');
    } finally {
      setLoading(false);
    }
  }, [level, token]);

  useEffect(() => {
    void loadCategories();
  }, [loadCategories]);

  const nextSortOrder = useMemo(() => {
    const regular = categories.filter((category) => !category.is_fallback);
    return regular.length === 0 ? 10 : Math.max(...regular.map((category) => category.sort_order)) + 10;
  }, [categories]);

  const startCreate = () => {
    setDraft({
      name: '',
      description: '',
      structures: [emptyStructure()],
      sortOrder: nextSortOrder,
      isActive: true,
      isFallback: false
    });
  };

  const startEdit = (category: W1GrammarCategory) => {
    setDraft({
      id: category.id,
      name: category.name,
      description: category.description,
      structures: category.structures.length > 0
        ? category.structures.map((structure) => ({ ...structure }))
        : [emptyStructure()],
      sortOrder: category.sort_order,
      isActive: category.is_active,
      isFallback: category.is_fallback
    });
  };

  const updateStructure = (index: number, key: keyof W1GrammarStructure, value: string) => {
    setDraft((current) => {
      if (!current) return current;
      return {
        ...current,
        structures: current.structures.map((structure, structureIndex) =>
          structureIndex === index ? { ...structure, [key]: value } : structure
        )
      };
    });
  };

  const saveDraft = async () => {
    if (!token || !draft) return;
    const name = draft.name.trim();
    if (!name) {
      setError('Grammar category name is required.');
      return;
    }

    const input: W1GrammarCategoryInput = {
      bankVersion: '2.0',
      level,
      name,
      description: draft.description.trim(),
      structures: draft.structures.filter((structure) =>
        structure.pattern.trim() || structure.example.trim() || structure.note.trim()
      ),
      sortOrder: draft.sortOrder,
      isActive: draft.isFallback ? true : draft.isActive
    };

    setSaving(true);
    setError('');
    try {
      if (draft.id) {
        await api.updateAdminW1GrammarCategory(token, draft.id, input);
      } else {
        await api.createAdminW1GrammarCategory(token, input);
      }
      setDraft(null);
      await loadCategories();
    } catch (saveError: any) {
      setError(saveError?.message || 'Failed to save grammar category.');
    } finally {
      setSaving(false);
    }
  };

  const deleteCategory = async (category: W1GrammarCategory) => {
    if (!token || category.is_fallback) return;
    if (!window.confirm(`Delete grammar category “${category.name}”?`)) return;
    setDeletingId(category.id);
    setError('');
    try {
      await api.deleteAdminW1GrammarCategory(token, category.id);
      await loadCategories();
    } catch (deleteError: any) {
      setError(deleteError?.message || 'Failed to delete grammar category.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-lg bg-white p-6 shadow">
        <button
          onClick={() => navigate(-1)}
          className="mb-3 inline-flex items-center text-sm font-medium text-slate-500 transition hover:text-indigo-600"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </button>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">W1 Grammar Categories</h1>
            <p className="mt-2 max-w-3xl text-sm text-slate-500">
              AI reads this live D1 configuration on every analysis. Changes take effect immediately without a deployment.
              One category can contain multiple related grammar structures.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <label className="text-sm font-medium text-slate-600">
              HSK 2.0 Level
              <select
                value={level}
                onChange={(event) => setSearchParams({ bankVersion: '2.0', level: event.target.value })}
                className="ml-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
              >
                <option value={3}>3</option>
                <option value={4}>4</option>
              </select>
            </label>
            <button
              onClick={startCreate}
              className="inline-flex items-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700"
            >
              <Plus className="mr-2 h-4 w-4" />
              Add Category
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      <div className="rounded-lg bg-white p-6 shadow">
        {loading ? (
          <div className="py-14 text-center text-slate-500">Loading grammar categories...</div>
        ) : (
          <div className="space-y-4">
            {categories.map((category) => (
              <div key={category.id} className="rounded-2xl border border-slate-200 p-5">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="text-lg font-bold text-slate-900">{category.name}</h2>
                      <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">
                        Order {category.sort_order}
                      </span>
                      {category.is_fallback && (
                        <span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-700">Fallback</span>
                      )}
                      {!category.is_active && (
                        <span className="rounded-full bg-red-100 px-2.5 py-1 text-xs font-semibold text-red-700">Inactive</span>
                      )}
                    </div>
                    {category.description && <p className="mt-2 text-sm leading-6 text-slate-600">{category.description}</p>}
                    {category.structures.length > 0 && (
                      <div className="mt-3 space-y-2">
                        {category.structures.map((structure, index) => (
                          <div key={`${category.id}-${index}`} className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600">
                            <span className="font-semibold text-slate-800">{structure.pattern || 'Structure'}</span>
                            {structure.example && <span> · {structure.example}</span>}
                            {structure.note && <span className="text-slate-400"> · {structure.note}</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => startEdit(category)}
                      className="inline-flex items-center rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                    >
                      <Pencil className="mr-2 h-4 w-4" /> Edit
                    </button>
                    {!category.is_fallback && (
                      <button
                        onClick={() => void deleteCategory(category)}
                        disabled={deletingId === category.id}
                        className="inline-flex items-center rounded-lg border border-red-200 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                      >
                        <Trash2 className="mr-2 h-4 w-4" /> Delete
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {draft && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
          <div className="max-h-[92vh] w-full max-w-5xl overflow-y-auto rounded-2xl bg-white shadow-2xl">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white px-6 py-4">
              <div>
                <h2 className="text-xl font-bold text-slate-900">{draft.id ? 'Edit Grammar Category' : 'Add Grammar Category'}</h2>
                <p className="mt-1 text-sm text-slate-500">HSK 2.0 · Level {level}</p>
              </div>
              <button onClick={() => setDraft(null)} className="rounded-full p-2 text-slate-500 hover:bg-slate-100">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-5 p-6">
              <div className="grid gap-4 md:grid-cols-[1fr_140px_140px]">
                <label className="text-sm font-medium text-slate-700">
                  Category name
                  <input
                    value={draft.name}
                    disabled={draft.isFallback}
                    onChange={(event) => setDraft({ ...draft, name: event.target.value })}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 disabled:bg-slate-100"
                  />
                </label>
                <label className="text-sm font-medium text-slate-700">
                  Sort order
                  <input
                    type="number"
                    value={draft.sortOrder}
                    disabled={draft.isFallback}
                    onChange={(event) => setDraft({ ...draft, sortOrder: Number(event.target.value) })}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 disabled:bg-slate-100"
                  />
                </label>
                <label className="flex items-center gap-2 pt-7 text-sm font-medium text-slate-700">
                  <input
                    type="checkbox"
                    checked={draft.isFallback ? true : draft.isActive}
                    disabled={draft.isFallback}
                    onChange={(event) => setDraft({ ...draft, isActive: event.target.checked })}
                    className="rounded border-slate-300"
                  />
                  Active
                </label>
              </div>

              <label className="block text-sm font-medium text-slate-700">
                Category description
                <textarea
                  value={draft.description}
                  onChange={(event) => setDraft({ ...draft, description: event.target.value })}
                  rows={3}
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                />
              </label>

              <div>
                <div className="mb-3 flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-slate-900">Grammar structures</h3>
                    <p className="text-sm text-slate-500">Add as many related structures as this category needs.</p>
                  </div>
                  <button
                    onClick={() => setDraft({ ...draft, structures: [...draft.structures, emptyStructure()] })}
                    className="inline-flex items-center rounded-lg border border-indigo-200 px-3 py-2 text-sm font-medium text-indigo-600 hover:bg-indigo-50"
                  >
                    <Plus className="mr-2 h-4 w-4" /> Add Structure
                  </button>
                </div>

                <div className="space-y-3">
                  {draft.structures.map((structure, index) => (
                    <div key={index} className="grid gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 md:grid-cols-3">
                      <input
                        value={structure.pattern}
                        onChange={(event) => updateStructure(index, 'pattern', event.target.value)}
                        placeholder="Structure, e.g. S + 把 + O + V"
                        className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                      />
                      <input
                        value={structure.example}
                        onChange={(event) => updateStructure(index, 'example', event.target.value)}
                        placeholder="Example sentence"
                        className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                      />
                      <div className="flex gap-2">
                        <input
                          value={structure.note}
                          onChange={(event) => updateStructure(index, 'note', event.target.value)}
                          placeholder="Note"
                          className="min-w-0 flex-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                        />
                        <button
                          onClick={() => setDraft({
                            ...draft,
                            structures: draft.structures.filter((_, structureIndex) => structureIndex !== index)
                          })}
                          className="rounded-lg p-2 text-red-500 hover:bg-red-50"
                          aria-label="Remove structure"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 flex justify-end gap-3 border-t border-slate-200 bg-white px-6 py-4">
              <button onClick={() => setDraft(null)} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700">
                Cancel
              </button>
              <button
                onClick={() => void saveDraft()}
                disabled={saving}
                className="inline-flex items-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                <Save className="mr-2 h-4 w-4" /> {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminW1GrammarCategories;

